/**
 * Shared owner-device-key glue for the member app's decryption surfaces (senior
 * review, reply reading). The heavy crypto (HPKE unwrap) lives in the vendored
 * `crypto/cek.js`; this is only browser-standard glue — reading the owner's
 * X25519 private key from IndexedDB (the exact store passkey enrollment writes)
 * and an AES-GCM decrypt — copied verbatim from the proven
 * `src/client/recipient/decrypt.ts` so every owner-decrypt path behaves alike.
 */

const INDEXED_DB_NAME = 'legacy-architect-keys';
const INDEXED_DB_STORE = 'identity-keys';
const INDEXED_DB_VERSION = 1;

export async function aesGcmDecrypt(args: {
  key: Uint8Array;
  iv: Uint8Array;
  ciphertext: Uint8Array;
  aad: Uint8Array;
}): Promise<Uint8Array> {
  const subtle = globalThis.crypto.subtle;
  const cryptoKey = await subtle.importKey('raw', toArrayBuffer(args.key), { name: 'AES-GCM' }, false, [
    'decrypt',
  ]);
  const pt = await subtle.decrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(args.iv), additionalData: toArrayBuffer(args.aad), tagLength: 128 },
    cryptoKey,
    toArrayBuffer(args.ciphertext),
  );
  return new Uint8Array(pt);
}

interface IDBLikeFactory {
  open(name: string, version?: number): IDBOpenDBRequest;
}

/**
 * This device has a session but not your key.
 *
 * A distinct type because the answer is specific and used to be wrong. The old
 * message said "sign in on this device first", which was reasonable when a
 * passkey was the only way in — having a session and having a key arrived
 * together. Now that you can sign in with a password from anywhere, they come
 * apart routinely: the account travels, the key does not. What fixes it is the
 * Recovery Kit, and a screen that says the wrong thing here sends someone to
 * sign in again, which will not help however many times they do it.
 */
export class MissingDeviceKeyError extends Error {
  public constructor() {
    super('This device does not have your key yet. Enter your Recovery Kit to unlock your messages here.');
    this.name = 'MissingDeviceKeyError';
  }
}

export async function loadOwnerPrivateKey(email: string): Promise<CryptoKey> {
  const indexedDb = (globalThis as { indexedDB?: IDBLikeFactory }).indexedDB;
  if (indexedDb === undefined) {
    throw new Error('IndexedDB is unavailable. This action requires a browser.');
  }
  return new Promise<CryptoKey>((resolve, reject) => {
    const req = indexedDb.open(INDEXED_DB_NAME, INDEXED_DB_VERSION);
    req.onupgradeneeded = (): void => {
      const db = req.result;
      if (!db.objectStoreNames.contains(INDEXED_DB_STORE)) {
        db.createObjectStore(INDEXED_DB_STORE, { keyPath: 'email' });
      }
    };
    req.onsuccess = (): void => {
      const db = req.result;
      const tx = db.transaction(INDEXED_DB_STORE, 'readonly');
      const store = tx.objectStore(INDEXED_DB_STORE);
      const getReq = store.get(email);
      getReq.onsuccess = (): void => {
        const v = getReq.result as { email: string; key: CryptoKey } | undefined;
        if (v === undefined || v.key === undefined) {
          reject(new MissingDeviceKeyError());
          return;
        }
        resolve(v.key);
      };
      getReq.onerror = (): void => reject(getReq.error ?? new Error('IndexedDB read failed'));
    };
    req.onerror = (): void => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

export function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}
